import { Box, Tooltip } from '@mui/material';
import { FC } from 'react';

import { SUB_TEXT_COLOR, SUB_TEXT_WEIGHT, SUPPLEMENT_TEXT_COLOR } from './unassignedTextColors';

export type InfoMetricEntry = { key: string; label: string; value: string };

/**
 * 選択されているのに値が無い項目に出す文字。
 *
 * Why not 行ごと消す: 値の有無はカードによって違うので、消すと「チェックしたのに
 * 何も変わらない」ように見える（ユーザー指摘 2026-09-01「任意項目の選択状態が
 * 未割当案件カードに反映されない」）。選択が効いていることはラベルで示す。
 */
export const EMPTY_INFO_VALUE = '—';

type Props = {
  entries: InfoMetricEntry[];
  /** ツールチップの出る向き。未割当リストは左に出す（リストが画面右端にあるため） */
  placement?: 'left' | 'bottom';
};

/**
 * 未割当リストの表示情報。ラベル付きの 2 列グリッドで出す。
 *
 * Why not ' / ' 区切りの 1 行（旧実装）: 項目が 4 → 24 に増えて、値だけが並んでも
 * どれが何の項目か読み取れなくなった。値が省略記号に埋もれると、ホバーしても
 * 9px の細い行しか当たり判定が無くツールチップも出したり出なかったりになる
 * （ユーザー指摘 2026-09-01）。グリッドにすると当たり判定がカード幅×行数に広がる。
 *
 * 1 件のときだけ 1 列にするのは、2 列固定だと右半分が空くため。
 */
export const InfoMetricList: FC<Props> = ({ entries, placement = 'left' }) => {
  if (entries.length === 0) return null;

  const tooltip = (
    <Box sx={{ '& > * + *': { mt: 0.25 } }}>
      {entries.map((entry) => (
        <Box component="p" key={entry.key} sx={{ m: 0 }}>
          <Box component="span" sx={{ opacity: 0.7 }}>{entry.label}:</Box>{' '}
          <span>{entry.value}</span>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip
      title={tooltip}
      placement={placement}
      enterDelay={200}
      slotProps={{ tooltip: { sx: { maxWidth: 300, fontSize: 11, lineHeight: 1.5 } } }}
    >
      <Box
        sx={{
          mt: 0.5,
          display: 'grid',
          gridTemplateColumns: entries.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          columnGap: 1,
          rowGap: '2px',
        }}
      >
        {entries.map((entry) => (
          <Box key={entry.key} sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Box
              component="span"
              data-testid="info-metric-label"
              sx={{
                flexShrink: 0,
                px: 0.5,
                borderRadius: '3px',
                bgcolor: 'hsl(var(--secondary) / 0.7)',
                color: SUPPLEMENT_TEXT_COLOR,
                fontSize: 9,
                fontWeight: SUB_TEXT_WEIGHT,
                lineHeight: 1.5,
                whiteSpace: 'nowrap',
              }}
            >
              {entry.label}
            </Box>
            <Box
              component="span"
              data-testid="info-metric-value"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 11,
                lineHeight: 1.5,
                fontWeight: SUB_TEXT_WEIGHT,
                color: SUB_TEXT_COLOR,
              }}
            >
              {entry.value}
            </Box>
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
};
